import React, { useState, useEffect } from "react"
import { Card, CardBody, CardTitle, Button, FormGroup } from "reactstrap"

import { formatAddress, formatEpochSeconds, formatTokenURI, numberWithCommas, chainOptions } from "../utils"
import { useWallet } from "../libs/useMetaMaskWallet"
import { useToast } from "../hooks/toast"
import SubmitCancelModal from "../Components/SubmitCancelModal"
import CustomSelect from "../Components/CustomSelect"
import BreadCrumb from "../Components/BreadCrumb"
import Tooltip from "../Components/Tooltip"

const apiUrlReserveSBT = "https://gating.idexo.io/?path=reservetoken"

// NOTE* props.location.state will be undefined if the user visits this dynamic route directly without clicking on the Link component in TokenGating.js page
const TokenGatingDetail = (props) => {
    const { token, metadata, subdomain } = props?.location?.state
    const wallet = useWallet()
    const toast = useToast()
    console.log("TokenGatingDetail token, metadata", token, metadata, subdomain)

    const [reservedSBTv2Chain, setReservedSBTv2Chain] = useState("") // string- if user has a reserved SBT version 2, this will be the chain
    const [showReserveEarlyAdopterV2Modal, setShowReserveEarlyAdopterV2Modal] = useState(false)
    const [chain, setChain] = useState("")
    const [showErrorRequired, setShowErrorRequired] = useState(false) // if user tries to submit modal without selecting chain
    const [modalAlertMessage, setModalAlertMessage] = useState("")
    const [modalAlertColor, setModalAlertColor] = useState("") // "danger" = red, "success" = green
    const [showSpinnerModalSubmit, setShowSpinnerModalSubmit] = useState(false)

    const checkUserReservedSBTv2 = async () => {
        try {
            const res = await fetch(apiUrlReserveSBT, {
                method: "GET",
                headers: { token: localStorage.jwtToken }
            })
            const response = await res.json()
            // console.log("checkUserReservedSBTv2", response)

            if (response.error || response.code !== 200) return console.log(response.error)

            // NOTE* response.data will be undefined if user has not previously reserved an SBT v2
            setReservedSBTv2Chain(response?.data?.S || "")
        } catch (err) {
            console.log(err)
        }
    }

    // for reserve early adopter SBT modal
    const handleClickSubmit = async () => {
        if (!chain) return setShowErrorRequired(true)
        setShowErrorRequired(false)
        setShowSpinnerModalSubmit(true)
        setModalAlertMessage("")
        try {
            const res = await fetch(apiUrlReserveSBT, {
                method: "POST",
                headers: { token: localStorage.jwtToken },
                body: JSON.stringify({ chain: chain.toLowerCase() })
            })
            const response = await res.json()

            if (response.error || response.code !== 200) {
                setModalAlertMessage("Error: " + response.error)
                setShowSpinnerModalSubmit(false)
                setModalAlertColor("danger")
                return
            }
            setModalAlertColor("success")
            setModalAlertMessage("Successfully reserved SBT! Stay tuned for upcoming details")
            // close modal after 3 seconds
            setTimeout(() => {
                setShowReserveEarlyAdopterV2Modal(false)
            }, 3000)
        } catch (err) {
            console.log(err)
            setModalAlertColor("danger")
            setModalAlertMessage(err)
        }
        setShowSpinnerModalSubmit(false)
    }

    const handleCloseReserveTokenModal = () => {
        setShowReserveEarlyAdopterV2Modal(false)
        setChain("")
        setShowErrorRequired(false)
    }

    // TO DO: move to utils.js for reuse/DRY
    const handleCopyClick = (e, address) => {
        e.stopPropagation()
        navigator.clipboard.writeText(address?.replace("×", "x"))
        toast.addSuccess(`Address ${formatAddress(address)} is copied to clipboard.`)
    }

    useEffect(async () => {
        if (subdomain === "app") checkUserReservedSBTv2()
    }, [reservedSBTv2Chain])

    return (
        <div className="content token-gating-detail-page">
            <BreadCrumb items={["Token Gating", "Token Details"]} links={["tokengating"]} />
            <Card className="token-gating-detail-card">
                <CardBody>
                    <CardTitle className="h3">{token?.name || token?.poolName || metadata?.name}</CardTitle>

                    <div className="image-details-wrap">
                        <div className="image">
                            {token.tokenURI || metadata?.image ? (
                                <img
                                    src={
                                        metadata?.image?.replace("ipfs://", "https://nftstorage.link/ipfs/") ||
                                        token?.image?.replace("ipfs://", "https://nftstorage.link/ipfs/") ||
                                        metadata?.uri?.replace("ipfs://", "https://nftstorage.link/ipfs/") ||
                                        ""
                                    }
                                    alt="token image"
                                    className="token-image"
                                />
                            ) : (
                                <i className="bx bx-image-alt" title="no token image" />
                            )}
                        </div>
                        <div className="details-reserve-wrap">
                            <div className="details">
                                <div className="address-wrap">
                                    <span className="label">Contract Address</span>
                                    <span className="value">
                                        {/* hard coding SOB collection contract address for now */}
                                        {/* {formatAddress(token.contract || "0x51bb9b9ccdf04af385b8356f41e20140badf80a0")} */}
                                        {formatAddress(
                                            subdomain === "bionicowls"
                                                ? "0x1078153c6a824e997c2d9c25889bD107e1637495"
                                                : subdomain === "swordsofblood"
                                                  ? "0x51bb9b9ccdf04af385b8356f41e20140badf80a0"
                                                  : subdomain === "nfttallinn"
                                                    ? "0x00b9259d1c75e47de787d64b64d40e2993734457"
                                                    : token.contract
                                        )}
                                        <Tooltip title="Copy contract address to clipboard" placement="bottom">
                                            <i
                                                className="bx bx-copy font-size-20"
                                                onClick={(e) =>
                                                    handleCopyClick(
                                                        e,
                                                        subdomain === "bionicowls"
                                                            ? "0x1078153c6a824e997c2d9c25889bD107e1637495"
                                                            : subdomain === "nfttallinn"
                                                              ? "0x00b9259d1c75e47de787d64b64d40e2993734457"
                                                              : subdomain === "swordsofblood"
                                                                ? "0x51bb9b9ccdf04af385b8356f41e20140badf80a0"
                                                                : token.contract || token.address
                                                    )
                                                }
                                            />
                                        </Tooltip>
                                    </span>
                                </div>
                                <div className="address-wrap">
                                    <span className="label">Owner Address</span>
                                    <span className="value">
                                        {formatAddress(token.owner || wallet.account)}
                                        <Tooltip title="Copy owner address to clipboard" placement="bottom">
                                            <i
                                                className="bx bx-copy font-size-20"
                                                onClick={(e) => handleCopyClick(e, token.owner || wallet.account)}
                                            />
                                        </Tooltip>
                                    </span>
                                </div>
                                <div>
                                    <span className="label">Blockchain</span>
                                    <span className="value">
                                        {subdomain === "bionicowls" || "nfttallinn"
                                            ? "arbitrumnova"
                                            : subdomain === "swordsofblood"
                                              ? "polygon"
                                              : token.chain}
                                    </span>
                                </div>
                                <div>
                                    <span className="label">Token ID</span>
                                    <span className="value">
                                        {token.tokenId ||
                                            (subdomain === "swordsofblood" || subdomain === "bionicowls" || subdomain === "nfttallinn"
                                                ? token.id
                                                : "")}
                                    </span>
                                </div>
                                <div>
                                    <span className="label">Collection Symbol</span>
                                    {/* <span className="value">{token.symbol || token.poolSymbol || "SOBDROP"}</span> */}
                                    <span className="value">
                                        {subdomain === "bionicowls"
                                            ? "BOST"
                                            : subdomain === "nfttallinn"
                                              ? "NFTEA"
                                              : subdomain === "swordsofblood"
                                                ? "SOBDROP"
                                                : token.symbol || token.poolSymbol}
                                    </span>
                                </div>
                                {(token.tokenURI || (subdomain === "swordsofblood" && token.uri)) && (
                                    <div className="metadatauri-wrap">
                                        <span className="label" title="token uri">
                                            Metadata URI
                                        </span>
                                        <span className="value">
                                            <a href={token.tokenURI || token.uri} target="_blank">
                                                {formatTokenURI(token.tokenURI || token.uri)}
                                            </a>
                                            <Tooltip title="Copy token uri" placement="bottom">
                                                <i
                                                    className="bx bx-copy font-size-20"
                                                    onClick={(e) => handleCopyClick(e, token.tokenURI || token.uri)}
                                                />
                                            </Tooltip>
                                        </span>
                                    </div>
                                )}
                                {/* only staking tokens have a .poolName attribute */}
                                {token.poolName && (
                                    <React.Fragment>
                                        <div>
                                            <span className="label">Staked Amount</span>
                                            <span className="value">{numberWithCommas(token.stakedAmount)}</span>
                                        </div>
                                        <div>
                                            <span className="label">Deposited At</span>
                                            <span className="value">{formatEpochSeconds(token.depositedAt)}</span>
                                        </div>

                                        {/* some staking tokens don't have a multiplier, but a lockedUntil, and compounding attribute */}
                                        {token.multiplier ? (
                                            <div>
                                                <span className="label">Multiplier</span>
                                                <span className="value">{token.multiplier}</span>
                                            </div>
                                        ) : (
                                            <React.Fragment>
                                                <div>
                                                    <span className="label">Locked Until</span>
                                                    <span className="value">{formatEpochSeconds(token.lockedUntil)}</span>
                                                </div>
                                                <div>
                                                    <span className="label">Compounding</span>
                                                    <span className="value">{token.compounding}</span>
                                                </div>
                                                <div>
                                                    <span className="label">Frequency</span>
                                                    {/* ex. "TRI-ANNUAL" */}
                                                    <span className="value">{token.type}</span>
                                                </div>
                                            </React.Fragment>
                                        )}
                                    </React.Fragment>
                                )}
                            </div>

                            {/* TO DO: check if user has an nft on MultiChain Gang on BSC, if they do, show them a link to the survey for MCGang holders on BSC */}
                            {/* only let user reserve v2 token if any of the conditions are met: 
                            - they have early adopter v1 token on bnbchain AND haven't already resrved an SBT v2
                            - they have a MultiChain Gang on Polygon AND haven't already resrved an SBT v2
                            - they have a MultiChain Gang on ETH AND haven't already resrved an SBT v2
                            - they have a Idexo Community Competition NFT on Avalanche AND haven't already resrved an SBT v2
                             */}
                            {((token.name === "idexo Early Adopter NFTs" && token.chain === "bnbchain" && !reservedSBTv2Chain) ||
                                (token.name === "MultiChain Gang on POLYGON" && token.chain === "polygon" && !reservedSBTv2Chain) ||
                                (token.name === "MultiChain Gang on ETH" && token.chain === "ethereum" && !reservedSBTv2Chain) ||
                                (token.name === "Idexo Community Competition NFT" && token.chain === "avalanche" && !reservedSBTv2Chain)) && (
                                <div className="reserve-v2-wrap">
                                    <Button
                                        color="primary"
                                        className="reserve-v2-btn"
                                        type="button"
                                        onClick={() => setShowReserveEarlyAdopterV2Modal(true)}
                                    >
                                        RESERVE EARLY ADOPTER V2 SBT
                                    </Button>
                                </div>
                            )}

                            {reservedSBTv2Chain && (
                                <div className="reserve-v2-wrap">
                                    <span>You have succesfully reserved an Early Adopter V2 SBT on {reservedSBTv2Chain}!</span>
                                </div>
                            )}

                            {subdomain === "swordsofblood" && (
                                <div className="subdomain-details-wrap">
                                    <div className="token-description">
                                        <span className="label">Token Description</span>
                                        <div>{metadata.description}</div>
                                    </div>

                                    <div className="token-attributes-wrap">
                                        <span className="label">Token Attributes</span>
                                        <div className="token-attributes">
                                            {metadata?.attributes?.map((attr, idx) => {
                                                return (
                                                    <div className="token-attribute" key={idx}>
                                                        <span className="attr-type">{attr?.trait_type}</span>
                                                        <span className="attr-val">{attr?.value}</span>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </CardBody>
            </Card>

            {showReserveEarlyAdopterV2Modal && (
                <SubmitCancelModal
                    isOpen={true}
                    closeModal={handleCloseReserveTokenModal}
                    handleSubmit={handleClickSubmit}
                    modalTitle="Reserve Early Adopter V2 Soulbound Token (SBT)"
                    alertColor={modalAlertColor}
                    alertMessage={modalAlertMessage}
                    showConfirmLoading={showSpinnerModalSubmit}
                >
                    <FormGroup className="mb-3">
                        <CustomSelect
                            name="chain-select"
                            label="Chain"
                            placeholder="Select Blockchain"
                            id="chain-select"
                            options={chainOptions}
                            selectedOption={chain}
                            required
                            errorMessage="This field is required"
                            showError={showErrorRequired}
                            // disabled={showSpinner}
                            handleChange={(value) => setChain(value)}
                        />
                    </FormGroup>
                </SubmitCancelModal>
            )}
        </div>
    )
}

export default TokenGatingDetail
